import { getDisplayPrice } from "@/modules/shop/util";

export const getStorefront = state => {
  return state.storefront;
};

export const getStorefrontBookId = state => {
  return state.storefront.book_id;
};

export const getStorefrontBookName = state => {
  return state.storefront.book_name;
};

export const getStorefrontSchoolName = state => {
  return state.storefront.school_name;
};

export const getCurrencyDecimals = state => {
  return state.storefront.currency_decimals;
};

export const getCurrencySymbol = state => {
  return state.storefront.currency_symbol;
};

export const getCurrencyCode = state => {
  return state.storefront.currency_code;
};

export const getStorefrontId = state => {
  return state.storefront.id;
};

export const getStorefrontShortcode = state => {
  return state.storefront.shortcode;
};

export const getStorefrontContactEmail = state => {
  return state.storefront.contact_email;
};

export const getStorefrontPublisherId = state => {
  return state.storefront.publisher_id;
};

export const getStorefrontPublisherName = state => {
  return state.storefront.publisher_name;
};

export const getStorefrontPublisherWebsite = state => {
  return state.storefront.publisher_website;
};

// products
export const getProducts = state => {
  return state.products;
};

export const storefrontHasProductsForSale = state => {
  return state.storefront.has_products_for_sale === true;
};

export const storefrontIsActive = state => {
  return state.storefront.is_active;
};

export const storefrontHasValidLicense = state => {
  return state.storefront.is_school_license_and_account_valid;
};

export const storefrontHasProducts = state => {
  return state.storefront.has_products === true;
};

export const storefrontUseStripeConnect = state => {
  return state.storefront.uses_stripe_connect;
};

export const storefrontIsStripeConnectOk = state => {
  return state.storefront.is_stripe_connect_ok;
};

// storefront config

export const getServiceFeeLabel = state => {
  return state.storefront.service_fee_label;
};

export const showServiceFees = state => {
  return state.storefront.show_service_fees;
};

export const getProductById = state => inStrProductId => {
  let index = state.products.findIndex(p => p.id === inStrProductId);
  if (index !== -1) {
    return state.products[index];
  } else {
    return [];
  }
};

// cart
export const getCart = state => {
  return state.cart;
};

// this returns a summary of items in the cart, including
// name, price, quantity and total
// it's meant for giving the user feedback before checkout, and does
// not include any taxes/shipping costs that may be added as part of the
// proper order workflow later on, which involves going via the API
export const getClientCartOrderSummary = (state, getters) => {
  if (state.cart.items.length > 0) {
    let total = 0;
    let i = 0;
    const items = state.cart.items.map(item => {
      i++;
      const product = getters.getProductById(item.productId);
      const price = product.price * item.quantity;
      total += price;
      return {
        ...item,
        key: i,
        name: product.name,
        price: price,
        displayPrice: getDisplayPrice(
          price,
          getters.getCurrencyDecimals,
          getters.getCurrencySymbol
        )
      };
    });

    return {
      items: items,
      total: total,
      displayTotal: getDisplayPrice(
        total,
        getters.getCurrencyDecimals,
        getters.getCurrencySymbol
      )
    };
  }
};

export const getCartItemsState = state => {
  return state.cart.items;
};

export const canOrderBeShipped = state => {
  return state.cart.items;
};

// internally, each product item in the cart has a quantity value. So,
// displaying the total count of items to the user is based upon total quantity,
// not total items
export const getCountOfItemsInCart = state => {
  let count = 0;
  state.cart.items.forEach(item => (count += item.quantity));
  return count;
};

// is the product out of stock? this will first check:
// a) the stock value reported from the API (when we fetch products)
// b) local inventory levels (i.e. based upon add to cart actions)
export const isProductOutOfStock = (state, getters) => inStrProductId => {
  const product = state.products.find(product => product.id === inStrProductId);
  if (product) {
    if (product.is_stock_tracked === false) {
      return false;
    } else {
      if (product.is_out_of_stock === true) {
        return true;
      } else {
        return getters._isProductOutOfStockLocally(inStrProductId);
      }
    }
  }
};

// determines if a product which is stocked controlled is out of stock locally
// note: this is a client side inventory test only, based upon items
// which have been added to the cart.
// it's main purpose is to block 'add to cart' when stock levels are zero.
// *actual* stock management is controlled by the server and enforced
// during checkout (if applicable)
export const _isProductOutOfStockLocally = state => inStrProductId => {
  const product = state.products.find(product => product.id === inStrProductId);
  if (product) {
    return (
      product.is_stock_tracked === true && state.inventory[product.id] === 0
    );
  }
  return false;
};

// order

export const getOrder = state => {
  return state.order;
};

export const getOrderEmail = state => {
  if (state.order.billing_address) {
    return state.order.billing_address.email;
  }
};

export const getOrderKids = state => {
  if (state.order.kids) {
    return state.order.kids;
  } else {
    return [];
  }
};

export const getOrderStripePaymentIntentId = state => {
  return state.order.stripe_payment_intent_id;
};

export const getOrderStripePaymentIntentClientSecret = state => {
  return state.order.stripe_payment_intent_client_secret;
};

export const getOrderAddress = state => {
  return state.order.billing_address;
};

export const getOrderAddressCondensed = state => {
  if (state.order.billing_address) {
    const address = state.order.billing_address;
    return `${address.address_line_1 || ""},${address.address_city ||
      ""} ${address.address_state || ""} ${address.address_zip ||
      ""},${address.address_country || ""}`;
  }
};

export const getOrderShippingAddressCondensed = state => {
  if (state.order.shipping_address) {
    const address = state.order.shipping_address;
    return `${address.shipping_address_1 || ""},${address.shipping_city ||
      ""} ${address.shipping_province || ""} ${address.shipping_postcode ||
      ""},${address.shipping_country || ""}`;
  }
};

export const getOrderId = state => {
  return state.order.id;
};

export const getOrderInvoiceNumber = state => {
  return state.order.invoice_no;
};

export const hasOrder = state => {
  return state.order.id !== null;
};

// meta

export const getParentPasscode = state => {
  return state.meta.parent_passcode;
};

export { getField } from "vuex-map-fields";
